import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';

const STAFFING_BOARD_API = '/api/v1/staffing-board';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface WorkPackageCert {
  id: number;
  cert_type: string;
  cert_name: string;
  status: string;
  expiry_date: string | null;
}

export interface WorkPackageCrewMember {
  assignment_id: number;
  tech_id: number;
  name: string;
  role: string;
  start_date: string;
  end_date: string | null;
  override_reason?: string;
  override_by?: number | null;
  override_at?: string | null;
  certifications: WorkPackageCert[];
  url: string;
}

export interface WorkPackageClient {
  id: number;
  name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  address: string;
  url: string;
}

export interface WorkPackageSite {
  location_fk_id: number | null;
  location_fk_name: string | null;
  address: string;
  city: string;
  state_province: string;
  url: string | null;
}

export interface WorkPackageJSA {
  id: number;
  title: string;
  date: string;
  status: string;
  url: string;
}

export interface WorkPackageSWP {
  id: number;
  title: string;
  date: string;
  status: string;
  url: string;
}

export interface WorkPackageRescueEquipment {
  id: number;
  equipment_name: string;
  quantity: number;
  location_staged: string;
}

export interface WorkPackageRescuePlan {
  id: number;
  title: string;
  date: string;
  status: string;
  rescue_equipment: WorkPackageRescueEquipment[];
  nearest_hospital: string;
  emergency_services_phone: string;
  url: string;
}

export interface WorkPackageDiagram {
  id: number;
  title: string;
  caption: string;
  parent_type: 'swp' | 'rescue_plan';
  parent_id: number;
}

export interface WorkPackageGearRequest {
  id: number;
  status: string;
  priority: string;
  needed_by: string | null;
  notes: string;
  url: string;
}

export interface WorkPackageGearDeployed {
  deployment_id: number;
  asset_id: number;
  asset_name: string;
  serial_number: string;
  status: string;
  next_inspection_due: string | null;
  url: string;
}

export interface WorkPackageGearManifestRequest {
  id: number;
  label: string;
  needed_by: string | null;
  priority: string;
  url: string;
}

export interface WorkPackageGearManifestAsset {
  deployment_id: number;
  asset_id: number;
  asset_name: string;
  serial_number: string;
  next_inspection_due: string | null;
  url: string | null;
}

export interface WorkPackageGearManifest {
  requested: WorkPackageGearManifestRequest[];
  staged: WorkPackageGearManifestRequest[];
  in_transit: WorkPackageGearManifestRequest[];
  received: WorkPackageGearManifestRequest[];
  deployed: WorkPackageGearManifestAsset[];
  inspection_due: WorkPackageGearManifestAsset[];
}

export interface WorkPackageRescueKitEquipment {
  id: number;
  equipment_name: string;
  quantity: number;
  location_staged: string;
  asset_id: number | null;
  asset_name: string | null;
  url: string | null;
  next_inspection_due: string | null;
  inspection_overdue: boolean;
}

export interface WorkPackageRescueKitReadiness {
  rescue_plan_id: number;
  rescue_plan_title: string;
  rescue_plan_status: string;
  rescue_plan_url: string;
  kit_status: 'ready' | 'inspection_due' | 'no_data';
  equipment: WorkPackageRescueKitEquipment[];
}

export interface WorkPackageComplianceLink {
  id: number;
  title: string;
  evidence_type: string;
  external_url: string;
  valid_from: string | null;
  valid_until: string | null;
}

export interface WorkPackageEmergencyContact {
  tech_id: number;
  contact_name: string;
  relationship: string;
  phone: string;
}

export interface WorkPackageDocument {
  id: number;
  name: string;
  document_type: string;
  url: string | null;
}

export interface WorkPackageGap {
  key: string;
  label: string;
  severity: 'blocker' | 'warning';
}

export interface WorkPackageData {
  project: {
    id: number;
    name: string;
    project_number: string;
    status: string;
    start_date: string | null;
    end_date: string | null;
    department: string | null;
  };
  client: WorkPackageClient | null;
  site: WorkPackageSite;
  crew: WorkPackageCrewMember[];
  jsas: WorkPackageJSA[];
  safe_work_plans: WorkPackageSWP[];
  rescue_plans: WorkPackageRescuePlan[];
  diagrams: WorkPackageDiagram[];
  gear_requests: WorkPackageGearRequest[];
  gear_deployed: WorkPackageGearDeployed[];
  gear_manifest: WorkPackageGearManifest;
  rescue_kit_readiness: WorkPackageRescueKitReadiness[];
  compliance_links: WorkPackageComplianceLink[];
  emergency_contacts: WorkPackageEmergencyContact[];
  documents: WorkPackageDocument[];
  gaps: WorkPackageGap[];
}

// ─── Query ───────────────────────────────────────────────────────────────────

export function useWorkPackage(projectId: number | null, enabled = true) {
  return useQuery<WorkPackageData>({
    queryKey: queryKeys.staffingBoard.workPackage(projectId ?? 0),
    queryFn: async () => {
      const resp = await apiClient.get<WorkPackageData>(
        `${STAFFING_BOARD_API}/work_package/`,
        { params: { project_id: projectId } },
      );
      return resp.data;
    },
    enabled: enabled && projectId !== null,
  });
}
